import type { KeyboardEvent } from 'react';

type SelectControlProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<[string, string]>;
  id?: string;
};

function slugify(label: string) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

export function SelectControl({ label, value, onChange, options, id }: SelectControlProps) {
  const selectId = id ?? `select-${slugify(label)}`;
  const labelId = `${selectId}-label`;
  const segmented = options.length <= 3;

  const choose = (key: string) => {
    if (key !== value) onChange(key);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const keys = options.map(([key]) => key);
    const index = keys.indexOf(value);
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      choose(keys[(index + 1 + keys.length) % keys.length]);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      choose(keys[(index - 1 + keys.length) % keys.length]);
    } else if (event.key === 'Home') {
      event.preventDefault();
      choose(keys[0]);
    } else if (event.key === 'End') {
      event.preventDefault();
      choose(keys[keys.length - 1]);
    }
  };

  return (
    <div className="mb-6 last:mb-0">
      <p id={labelId} className="text-sm font-extrabold uppercase tracking-[0.14em] text-[#2d7a13]">
        {label}
      </p>
      <div
        id={selectId}
        role="radiogroup"
        aria-labelledby={labelId}
        aria-label={label}
        onKeyDown={onKeyDown}
        className={
          segmented
            ? 'mt-2 flex max-w-sm rounded-2xl bg-[#ECFFE6] p-1'
            : 'mt-2 flex flex-wrap gap-2'
        }
      >
        {options.map(([key, name]) => {
          const active = key === value;
          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={active ? 0 : -1}
              onClick={() => choose(key)}
              className={
                segmented
                  ? `min-h-11 flex-1 rounded-xl px-3 text-sm font-extrabold transition focus:outline-none focus-visible:ring-4 focus-visible:ring-[#399918]/25 ${
                      active ? 'bg-[#399918] text-white shadow-sm' : 'text-[#2d7a13] hover:bg-white/80'
                    }`
                  : `min-h-11 rounded-full px-4 text-sm font-extrabold transition focus:outline-none focus-visible:ring-4 focus-visible:ring-[#399918]/25 ${
                      active
                        ? 'bg-[#399918] text-white shadow-sm'
                        : 'bg-[#F3FBF0] text-hmong-ink ring-1 ring-[#cfe8c6] hover:bg-[#ECFFE6]'
                    }`
              }
            >
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
